import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button, TextField, List, Dialog, DialogTitle, DialogContent, DialogActions, Container, Stack, Box } from '@mui/material';
import { io, Socket } from 'socket.io-client';
import { sendMessage, getMessages } from '@services/chatService';
import './ConversationPage.scss';
import withUser from '@components/withUser';
import type { WithUserProps } from '@components/withUser';
import type { Message } from '@app-types/models/chat';
import { setActiveConversation } from '@utils/activeConversation';

interface ConversationPageProps extends WithUserProps {
  userId?: string;
  staffId?: string;
}

const ConversationPage: React.FC<ConversationPageProps> = ({ user, userId: propUserId, staffId }) => {
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const userId = propUserId || paramUserId;
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [socket, setSocket] = useState<Socket | null>(null);

  // Tell the app-wide notification socket which conversation is on screen, so it doesn't
  // toast messages the user is already watching arrive here (this view is also embedded
  // in the reservation modals, so the URL alone wouldn't reveal it).
  useEffect(() => {
    setActiveConversation(userId || null);
    return () => setActiveConversation(null);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // The token is REQUIRED: the server authorises `register` against it (a business may only
    // register as staff of its own salon), so a tokenless socket receives no messages. `auth`
    // must be a callback, not a captured value — the JWT expires in ~30m and is rotated, so
    // each (re)connection attempt re-reads the current one.
    const readToken = () =>
      sessionStorage.getItem('impersonation_token') || localStorage.getItem('token') || undefined;
    const socket = io(import.meta.env.VITE_CHAT_SOCKET_URL, {
      auth: (cb: (data: { token?: string }) => void) => cb({ token: readToken() }),
      reconnectionDelayMax: 30000,
    });
    setSocket(socket);

    // A middleware rejection (expired token) is fatal to socket.io — it sets `active = false`
    // and never retries. Reconnect so the auth callback re-reads the rotated token.
    let retries = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const onConnectError = () => {
      if (socket.active) return;
      retries += 1;
      retryTimer = setTimeout(() => socket.connect(), Math.min(30000, 2000 * 2 ** (retries - 1)));
    };
    socket.on('connect_error', onConnectError);
    const fetchMessages = async () => {
      try {
        const fetchedMessages = await getMessages(userId, staffId);
        setMessages(Array.isArray(fetchedMessages) ? fetchedMessages : []);
      } catch (error) {
        console.error('Error fetching messages:', error);
      }
    };

    fetchMessages();

    function onConnect() {
      console.log('Socket connected:', socket.id);
      //for business account also is a stylist

      if (staffId) {
        socket.emit('register', staffId);
      } else {
        if (user.role === 'business' && user.staffId) {
          socket.emit('register', user.staffId as string);
        } else {
          socket.emit('register', user._id);
        }
      }
    }

    function onDisconnect() {
      console.log('Socket disconnected:', socket.id);
    }

    function onReceiveMessage(message: Message) {
      console.log('Received message:', message);
      setMessages((prevMessages) => [...prevMessages, message]);
    }

    function onError(error: unknown) {
      console.error('Socket error:', error);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('receiveMessage', onReceiveMessage);
    socket.on('error', onError);
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('disconnect', onDisconnect);
      socket.off('receiveMessage', onReceiveMessage);
      socket.off('error', onError);
    };
  }, [userId, user, staffId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView();
    }
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView();
    }
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (newMessage.trim() || selectedImage) {
      const formData = new FormData();
      if (staffId) {
        formData.append('senderId', staffId);
        formData.append('senderRole', 'stylist');
      }
      formData.append('receiverId', userId!);
      formData.append('receiverRole', 'customer');
      formData.append('content', newMessage);

      let imageUrl: string | null = null;
      if (selectedImage) {
        formData.append('files', selectedImage);
        imageUrl = URL.createObjectURL(selectedImage);
      }

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: { _id: user._id },
          receiverId: userId!,
          receiverRole: 'customer',
          content: newMessage,
          media: imageUrl ? [{ mediaType: 'image', key: imageUrl }] : [],
        },
      ]);

      try {
        const message = await sendMessage(formData);
        socket?.emit('sendMessage', {
          senderId: user._id,
          senderRole: 'stylist',
          receiverId: userId,
          receiverRole: 'customer',
          content: newMessage,
          media: message.media,
        });
      } catch (error) {
        console.error('Error saving message:', error);
      }

      setNewMessage('');
      setSelectedImage(null);
      setShowModal(false);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setShowModal(true);
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setFullscreenImage(imageUrl);
    setShowFullscreenModal(true);
  };

  if (!userId) {
    return null;
  }

  return (
    <Container className="conversation-container" sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)' }}>
      <List className="conversation-messages" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 2 }}>
        {messages.map((msg, index) => (
          <Box key={index} className={`list-group-item ${msg.sender?._id !== userId ? 'my-message' : 'their-message'}`}>
            {msg.content}
            {msg.media && msg.media.map((mediaItem, mediaIndex) => (
              mediaItem.mediaType === 'image' && (
                <img
                  key={mediaIndex}
                  src={mediaItem.key.startsWith('blob:') ? mediaItem.key : `${import.meta.env.VITE_R2_CHAT_ENDPOINT}/${mediaItem.key}`}
                  alt="uploaded"
                  style={{ maxWidth: '100%', marginTop: '10px', cursor: 'pointer' }}
                  onClick={() => handleImageClick(mediaItem.key.startsWith('blob:') ? mediaItem.key : `${import.meta.env.VITE_R2_CHAT_ENDPOINT}/${mediaItem.key}`)}
                />
              )
            ))}
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </List>
      <Box component="form" onSubmit={handleSendMessage} className="conversation-form" sx={{ position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 10, p: 1, boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.1)' }}>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={handleFileButtonClick}>
            +
          </Button>
          <TextField
            type="text"
            value={newMessage}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            fullWidth
          />
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
          <Button type="submit" variant="contained" color="primary">
            Send
          </Button>
        </Stack>
      </Box>

      <Dialog open={showModal} onClose={() => setShowModal(false)}>
        <DialogTitle>Preview Image</DialogTitle>
        <DialogContent>
          {selectedImage && (
            <img
              src={URL.createObjectURL(selectedImage)}
              alt="Selected"
              style={{ maxWidth: '100%' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSendMessage} color="primary">
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showFullscreenModal} onClose={() => setShowFullscreenModal(false)}>
        <DialogContent>
          {fullscreenImage && (
            <img
              src={fullscreenImage}
              alt="Fullscreen"
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

const WrappedConversationPage = withUser(ConversationPage);
export default WrappedConversationPage;